import { OrganizationPage } from "@features/organization/OrganizationPage";
import { SalesDirectorPage } from "@features/sales-reporting/SalesDirectorPage";
import { MyTeamPage } from "@features/sales-reporting/MyTeamPage";
import { SalesReportingPage } from "@features/sales-reporting/SalesReportingPage";
import { lazy, Suspense } from "react";
import { createBrowserRouter, Navigate, Outlet } from "react-router-dom";
import { LoadingState } from "@components/feedback/AsyncState";
import { ErrorPage } from "@components/feedback/ErrorPage";
import { RouteErrorPage } from "@app/router/RouteErrorPage";
import { UnavailablePage } from "@components/feedback/UnavailablePage";
import { PortalLayout } from "@components/layout/PortalLayout";
import { LoginPage } from "@features/auth/pages/LoginPage";
import { ResetPasswordPage } from "@features/auth/pages/ResetPasswordPage";
import {
  PermissionRoute,
  ProtectedRoute,
  PublicRoute,
} from "@app/router/RouteGuards";

const DashboardPage = lazy(() =>
  import("@features/dashboard/pages/dashboard").then((module) => ({
    default: module.DashboardPage,
  })),
);
const CalendarPage = lazy(() =>
  import("@features/calendar/pages/CalendarPage").then((module) => ({
    default: module.CalendarPage,
  })),
);
const PropertyListPage = lazy(() =>
  import("@features/properties/pages/PropertyListPage").then((module) => ({
    default: module.PropertyListPage,
  })),
);
const PropertyDetailPage = lazy(() =>
  import("@features/properties/pages/PropertyDetailPage").then((module) => ({
    default: module.PropertyDetailPage,
  })),
);
const ProfilePage = lazy(() =>
  import("@features/profile/pages/ProfilePage").then((module) => ({
    default: module.ProfilePage,
  })),
);
const LeadListPage = lazy(() =>
  import("@features/leads/pages/LeadListPage").then((module) => ({
    default: module.LeadListPage,
  })),
);
const LeadDetailPage = lazy(() =>
  import("@features/leads/pages/LeadDetailPage").then((module) => ({
    default: module.LeadDetailPage,
  })),
);
const LeadPipelinePage = lazy(() =>
  import("@features/leads/pages/LeadPipelinePage").then((module) => ({
    default: module.LeadPipelinePage,
  })),
);
const CreateLeadPage = lazy(() =>
  import("@features/leads/pages/CreateLeadPage").then((module) => ({
    default: module.CreateLeadPage,
  })),
);
const EditLeadPage = lazy(() =>
  import("@features/leads/pages/EditLeadPage").then((module) => ({
    default: module.EditLeadPage,
  })),
);
const LeadManagementRoute = lazy(() =>
  import("@features/leads/routes/LeadManagementRoute").then((module) => ({
    default: module.LeadManagementRoute,
  })),
);
const TeamManagementPage = lazy(() =>
  import("@features/users/pages/TeamManagementPage").then((module) => ({
    default: module.TeamManagementPage,
  })),
);
const UserDetailPage = lazy(() =>
  import("@features/users/pages/UserDetailPage").then((module) => ({
    default: module.UserDetailPage,
  })),
);
const FollowUpsPage = lazy(() =>
  import("@features/follow-ups/pages/FollowUpsPage").then((module) => ({
    default: module.FollowUpsPage,
  })),
);
const FavoritesPage = lazy(() =>
  import("@features/favorites/pages/FavoritesPage").then((module) => ({
    default: module.FavoritesPage,
  })),
);
const ActivityLogPage = lazy(() =>
  import("@features/activity/pages/ActivityLogPage").then((module) => ({
    default: module.ActivityLogPage,
  })),
);
const MasterDataPage = lazy(() =>
  import("@features/master-data/pages/MasterDataPage").then((module) => ({
    default: module.MasterDataPage,
  })),
);
const RolePermissionsPage = lazy(() =>
  import("@features/roles/pages/RolePermissionsPage").then((module) => ({
    default: module.RolePermissionsPage,
  })),
);
const KycApprovalPage = lazy(() =>
  import("@features/kyc/pages/KycApprovalPage").then((module) => ({
    default: module.KycApprovalPage,
  })),
);
const KycAgentPage = lazy(() =>
  import("@features/kyc/pages/KycAgentPage").then((module) => ({
    default: module.KycAgentPage,
  })),
);
const fallback = <LoadingState label="Loading page" />;

export const router = createBrowserRouter([
  {
    element: <Outlet />,
    errorElement: <RouteErrorPage />,
    children: [
      {
        element: <PublicRoute />,
        children: [
          { path: "/login", element: <LoginPage /> },
          { path: "/reset-password", element: <ResetPasswordPage /> },
        ],
      },
      {
        element: <ProtectedRoute />,
        children: [
          {
            element: <PortalLayout />,
            children: [
              { index: true, element: <Navigate to="/dashboard" replace /> },
              {
                path: "/dashboard",
                element: (
                  <PermissionRoute permission="dashboard:view">
                    <Suspense fallback={fallback}>
                      <DashboardPage />
                    </Suspense>
                  </PermissionRoute>
                ),
              },
              {
                path: "/calendar",
                element: (
                  <Suspense fallback={fallback}>
                    <CalendarPage />
                  </Suspense>
                ),
              },
              {
                path: "/properties",
                element: (
                  <PermissionRoute permission="properties:view">
                    <Suspense fallback={fallback}>
                      <PropertyListPage />
                    </Suspense>
                  </PermissionRoute>
                ),
              },
              {
                path: "/properties/:propertyId",
                element: (
                  <PermissionRoute permission="properties:view">
                    <Suspense fallback={fallback}>
                      <PropertyDetailPage />
                    </Suspense>
                  </PermissionRoute>
                ),
              },
              {
                path: "/my-properties",
                element: (
                  <PermissionRoute permission="properties:view">
                    <Suspense fallback={fallback}>
                      <PropertyListPage mine />
                    </Suspense>
                  </PermissionRoute>
                ),
              },
              {
                path: "/profile",
                element: (
                  <PermissionRoute permission="profile:view">
                    <Suspense fallback={fallback}>
                      <ProfilePage />
                    </Suspense>
                  </PermissionRoute>
                ),
              },
              {
                path: "/favorites",
                element: (
                  <PermissionRoute permission="favorites:view">
                    <Suspense fallback={fallback}>
                      <FavoritesPage />
                    </Suspense>
                  </PermissionRoute>
                ),
              },
              {
                path: "/leads",
                element: (
                  <PermissionRoute permission="leads:view">
                    <Suspense fallback={fallback}>
                      <LeadListPage />
                    </Suspense>
                  </PermissionRoute>
                ),
              },
              {
                path: "/leads/:leadId",
                element: (
                  <PermissionRoute permission="leads:view">
                    <Suspense fallback={fallback}>
                      <LeadDetailPage />
                    </Suspense>
                  </PermissionRoute>
                ),
              },
              {
                path: "/pipeline",
                element: (
                  <PermissionRoute permission="pipeline:view">
                    <Suspense fallback={fallback}>
                      <LeadPipelinePage />
                    </Suspense>
                  </PermissionRoute>
                ),
              },
              {
                element: (
                  <Suspense fallback={fallback}>
                    <LeadManagementRoute />
                  </Suspense>
                ),
                children: [
                  {
                    path: "/leads/create",
                    element: (
                      <Suspense fallback={fallback}>
                        <CreateLeadPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "/leads/:leadId/edit",
                    element: (
                      <Suspense fallback={fallback}>
                        <EditLeadPage />
                      </Suspense>
                    ),
                  },
                ],
              },
              {
                path: "/follow-ups",
                element: (
                  <PermissionRoute permission="followups:view">
                    <Suspense fallback={fallback}>
                      <FollowUpsPage />
                    </Suspense>
                  </PermissionRoute>
                ),
              },
              {
                path: "/meetings-site-visits",
                element: <UnavailablePage title="Meetings / Site Visits" />,
              },
              {
                path: "/kyc-management",
                element: (
                  <PermissionRoute permission="kyc:review">
                    <Suspense fallback={fallback}>
                      <KycApprovalPage />
                    </Suspense>
                  </PermissionRoute>
                ),
              },
              {
                path: "/leads/:leadId/kyc",
                element: (
                  <PermissionRoute permission="kyc:view">
                    <Suspense fallback={fallback}>
                      <KycAgentPage />
                    </Suspense>
                  </PermissionRoute>
                ),
              },
              {
                path: "/kyc-forms",
                element: <UnavailablePage title="KYC Forms" />,
              },
              {
                path: "/tickets",
                element: (
                  <PermissionRoute permission="tickets:view">
                    <UnavailablePage title="Listing Requests" />
                  </PermissionRoute>
                ),
              },
              { path: "/organization", element: <OrganizationPage /> },
              { path: "/sales-reporting", element: <SalesReportingPage /> },
              { path: "/my-team", element: <MyTeamPage /> },
              { path: "/sales-teams", element: <SalesDirectorPage /> },
              {
                path: "/notifications",
                element: <UnavailablePage title="Notifications" />,
              },
              {
                element: <PermissionRoute permission="users:view" />,
                children: [
                  {
                    path: "/team-management",
                    element: (
                      <Suspense fallback={fallback}>
                        <TeamManagementPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "/team-management/:userId",
                    element: (
                      <Suspense fallback={fallback}>
                        <UserDetailPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "/users",
                    element: <Navigate to="/team-management" replace />,
                  },
                  {
                    path: "/lead-assignments",
                    element: <UnavailablePage title="Lead Assignments" />,
                  },
                ],
              },
              {
                element: <PermissionRoute permission="reports:view" />,
                children: [
                  {
                    path: "/reports",
                    element: <Navigate to="/reports/leads" replace />,
                  },
                  {
                    path: "/reports/leads",
                    element: <UnavailablePage title="Lead Reports" />,
                  },
                  {
                    path: "/reports/sales-revenue",
                    element: <UnavailablePage title="Sales & Revenue" />,
                  },
                  {
                    path: "/reports/agent-performance",
                    element: <UnavailablePage title="Agent Performance" />,
                  },
                  {
                    path: "/team-performance",
                    element: <UnavailablePage title="Team Performance" />,
                  },
                ],
              },
              {
                path: "/tasks",
                element: <UnavailablePage title="Tasks" />,
              },
              {
                path: "/my-performance",
                element: <UnavailablePage title="My Performance" />,
              },
              {
                element: <PermissionRoute permission="roles:view" />,
                children: [
                  {
                    path: "/roles",
                    element: (
                      <Suspense fallback={fallback}>
                        <RolePermissionsPage />
                      </Suspense>
                    ),
                  },
                ],
              },
              {
                element: <PermissionRoute permission="master:view" />,
                children: [
                  {
                    path: "/master-data",
                    element: (
                      <Suspense fallback={fallback}>
                        <MasterDataPage />
                      </Suspense>
                    ),
                  },
                ],
              },
              {
                element: <PermissionRoute permission="logs:view" />,
                children: [
                  {
                    path: "/activity-logs",
                    element: (
                      <Suspense fallback={fallback}>
                        <ActivityLogPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "/api-logs",
                    element: <UnavailablePage title="API Logs" />,
                  },
                ],
              },
              {
                path: "/unauthorized",
                element: <ErrorPage kind="forbidden" />,
              },
            ],
          },
        ],
      },
      { path: "*", element: <ErrorPage kind="not-found" /> },
    ],
  },
]);
